import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Building2, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { fetchBrands, fetchJobs, selectHasActiveWork, selectBrandSearch } from '../../store/slices/brandsSlice';
import { useBrandUpdatesFinished } from '../../services/brandUpdates';
import { setShowAddProjectModal } from '../../store/slices/modalsSlice';
import { useAccount, canAddBrand, isRestrictedMember } from '../../services/account';
import LoadingBrands from './LoadingBrands';
import BrandCard from './BrandCard';

const PAGE_SIZE = 5;
const JOB_POLL_MS = 5000;
// Long enough that typing "swift casino" is one request, short enough that the
// list feels like it is following along.
const SEARCH_DEBOUNCE_MS = 300;

const BrandsSection = () => {
  const dispatch = useDispatch();
  const { brands, loading, loadingMore, total, matched, error, jobs, liveChecks } = useSelector(state => state.brands);
  const hasActiveWork = useSelector(selectHasActiveWork);
  const prevRunningIdsRef = useRef(new Set());
  const prevLiveIdsRef = useRef(new Set());

  // What is in the box, and what the loaded list belongs to. The ref is what
  // the background refetches below use: they fire from job polling, not from a
  // render, and must not quietly drop the user back to the unfiltered list.
  const [query, setQuery] = useState('');
  const searchRef = useRef('');
  const searching = query.trim() !== '';

  // Initial load: only the first page. The dashboard fan-out (analytics +
  // lost-affiliate calls) only fires for cards that mount, so this also caps
  // the per-card request storm on initial paint.
  useEffect(() => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: 0 }));
    // Pick up jobs already running server-side (e.g. after a page refresh)
    dispatch(fetchJobs());
  }, [dispatch]);

  // Searching is the server's job — only a page of brands is loaded, so
  // filtering here would search five cards and miss the rest. Skips the first
  // run: the empty term is what the initial load above already asked for.
  const firstSearchRun = useRef(true);
  useEffect(() => {
    if (firstSearchRun.current) {
      firstSearchRun.current = false;
      return;
    }
    const term = query.trim();
    const timeoutId = setTimeout(() => {
      if (term === searchRef.current) return;  // already the list on screen
      searchRef.current = term;
      dispatch(fetchBrands({ limit: PAGE_SIZE, offset: 0, search: term }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [query, dispatch]);

  // Adding a project reloads the list unfiltered, from the modal. Empty the box
  // when that happens, so it never claims to be filtering a list it isn't.
  const loadedSearch = useSelector(selectBrandSearch);
  useEffect(() => {
    if (loadedSearch !== searchRef.current) {
      searchRef.current = loadedSearch;
      setQuery(loadedSearch);
    }
  }, [loadedSearch]);

  // ONE polling interval for all brands (V1 created a broken interval per card,
  // stored in redux — the reason the progress bar froze until a refresh).
  useEffect(() => {
    if (!hasActiveWork) return;
    const intervalId = setInterval(() => {
      dispatch(fetchJobs());
    }, JOB_POLL_MS);
    return () => clearInterval(intervalId);
  }, [hasActiveWork, dispatch]);

  // When a job finishes, refetch the brand list once so the card's metrics
  // (live affiliates, untapped opportunities, …) fill in without a manual refresh.
  useEffect(() => {
    const runningIds = new Set(
      Object.values(jobs).filter(j => j.status === 'running').map(j => j.brand_id)
    );
    const prev = prevRunningIdsRef.current;
    const finished = [...prev].some(id => !runningIds.has(id));
    prevRunningIdsRef.current = runningIds;
    if (finished) {
      dispatch(fetchBrands({ limit: Math.max(PAGE_SIZE, brands.length), offset: 0, search: searchRef.current }));
    }
  }, [jobs, dispatch, brands.length]);

  // Same for the background live-affiliate check (the bulb): when it finishes it
  // may have removed non-live affiliates, so refresh the counts once more —
  // otherwise the card shows a stale number until a manual refresh.
  useEffect(() => {
    const liveIds = new Set(Object.keys(liveChecks || {}).map(Number));
    const prev = prevLiveIdsRef.current;
    const finished = [...prev].some(id => !liveIds.has(id));
    prevLiveIdsRef.current = liveIds;
    if (finished) {
      dispatch(fetchBrands({ limit: Math.max(PAGE_SIZE, brands.length), offset: 0, search: searchRef.current }));
    }
  }, [liveChecks, dispatch, brands.length]);

  // And again for a refresh run against one of these brands' DOMAINS — an admin
  // re-run from Update Brand rebuilds the shared affiliate list, so the counts
  // on a card can change without this account having started anything. Same
  // page-preserving refetch as the two above.
  useBrandUpdatesFinished(() => {
    dispatch(fetchBrands({ limit: Math.max(PAGE_SIZE, brands.length), offset: 0, search: searchRef.current }));
  });

  const handleAddProject = () => {
    dispatch(setShowAddProjectModal(true));
  };
  // Team members only add projects if their account admin allows it.
  const { account } = useAccount();
  const mayAddProject = canAddBrand(account, 'projects');

  const handleRetry = () => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: 0, search: searchRef.current }));
  };

  const handleLoadMore = () => {
    dispatch(fetchBrands({ limit: PAGE_SIZE, offset: brands.length, append: true, search: searchRef.current }));
  };

  const clearSearch = () => setQuery('');

  // matched === null means we don't know the count yet (legacy non-paginated
  // response). In that case never show the load-more button. It counts what
  // the search found; `total` stays the account's whole project count.
  const hasMore = typeof matched === 'number' && brands.length < matched;

  // No point in a search box for a handful of cards you can already see. Once
  // it is open it stays open while something is typed in it, however few
  // brands the search leaves on screen.
  const showSearch = searching || (typeof total === 'number' && total > PAGE_SIZE);

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-0.5">Your Projects</h2>
          <p className="text-gray-500 dark:text-slate-400 text-xs">Monitor and manage your affiliate brands</p>
        </div>
        {showSearch && (
          <div className="relative w-56 sm:w-72 shrink-0">
            <Search className="w-4 h-4 text-gray-400 dark:text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') clearSearch(); }}
              placeholder="Search projects…"
              aria-label="Search projects by name or domain"
              className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searching && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {loading && <LoadingBrands />}

      {!loading && error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="text-red-800 dark:text-red-400">
            <strong>Error:</strong> {error}
          </div>
          <button
            onClick={handleRetry}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && brands.length === 0 && searching && (
        <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700">
          <Search className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No projects match “{query.trim()}”</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-4">Try part of the brand name or its domain.</p>
          <button
            onClick={clearSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Clear search
          </button>
        </div>
      )}

      {!loading && !error && brands.length === 0 && !searching && (
        <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700">
          <Building2 className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">No brands found</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-4">
            {mayAddProject
              ? 'Get started by adding your first brand'
              : isRestrictedMember(account)
                ? 'No projects have been shared with you yet. Ask your account admin.'
                : 'No projects yet.'}
          </p>
          {mayAddProject && (
            <button
              onClick={handleAddProject}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Your First Brand
            </button>
          )}
        </div>
      )}

      {!loading && !error && brands.length > 0 && (
        <>
          <div className="space-y-3">
            {brands.map((brand) => (
              <BrandCard key={brand.id} brand={brand} />
            ))}
          </div>
          {searching && !hasMore && (
            <p className="mt-3 text-center text-xs text-gray-500 dark:text-slate-400">
              {brands.length === 1 ? '1 project matches' : `${brands.length} projects match`} “{query.trim()}”
            </p>
          )}
          {hasMore && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Load {Math.min(PAGE_SIZE, matched - brands.length)} more
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Showing {brands.length} of {matched} {searching ? 'matching brands' : 'brands'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrandsSection;
